import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Pencil, X, Plus, Trash2 } from 'lucide-react';
import { Resource } from '../../lib/resources';
import { supabase } from '../../lib/supabase';
import { getServices, Service } from '../../lib/services';

interface CompensationSectionProps {
  resource: Resource;
  editMode: string | null;
  formData: Partial<Resource>;
  setFormData: (data: Partial<Resource>) => void;
  onSave: (section: string) => void;
  onEdit: (section: string) => void;
  loading?: boolean;
}

export default function CompensationSection({
  resource,
  editMode,
  formData,
  setFormData,
  onSave,
  onEdit,
  loading
}: CompensationSectionProps) {
  const [isBonusModalOpen, setIsBonusModalOpen] = useState(false);
  const [bookingBonusEnabled, setBookingBonusEnabled] = useState(false);
  const [bookingBonusPercentage, setBookingBonusPercentage] = useState<number>(0);
  
  // Service bonus state
  const [serviceBonusEnabled, setServiceBonusEnabled] = useState(false);
  const [serviceBonusItems, setServiceBonusItems] = useState<{serviceId: string, percentage: number}[]>([]);
  const [availableServices, setAvailableServices] = useState<Service[]>([]);
  const [upsellEnabled, setUpsellEnabled] = useState(false);
  const [upsellPayout, setUpsellPayout] = useState<'creator' | 'split'>('creator');
  const [upsellApplyTo, setUpsellApplyTo] = useState<'anything' | 'selected'>('anything');
  const [upsellPercentage, setUpsellPercentage] = useState<number>(0);
  const [upsellServices, setUpsellServices] = useState<{serviceId: string, percentage: number}[]>([]);
  const [upsellAddons, setUpsellAddons] = useState<{addonId: string, percentage: number}[]>([]);
  const [availableAddons, setAvailableAddons] = useState<{id: string, name: string}[]>([]);

  // Load services when modal opens
  useEffect(() => {
    const loadServices = async () => {
      if (!isBonusModalOpen || !resource?.business_id) return;
      
      try {
        const services = await getServices(resource.business_id);
        setAvailableServices(services);
        const { data: addons } = await supabase
          .from('addon_library')
          .select('id, name')
          .eq('business_id', resource.business_id)
          .order('name');
        setAvailableAddons((addons || []).filter((addon) => addon.name));
      } catch (error) {
        console.error('Error loading services:', error);
      }
    };
    
    loadServices();
  }, [isBonusModalOpen, resource?.business_id]);

  // Load bonus configuration when modal opens
  useEffect(() => {
    const loadBonusConfiguration = async () => {
      if (!isBonusModalOpen || !resource?.id) return;
      
      try {
        const { data: dbResource, error } = await supabase
          .from('resources')
          .select('bonus_configuration')
          .eq('id', resource.id)
          .single();
        
        if (error) {
          console.error('Failed to load bonus configuration:', error);
          return;
        }
        
        const bonusConfig = dbResource?.bonus_configuration;
        const bookingBonus = bonusConfig?.booking_bonus;
        const serviceBonus = bonusConfig?.service_bonus;
        
        // Load booking bonus
        if (bookingBonus) {
          setBookingBonusEnabled(bookingBonus.enabled || false);
          setBookingBonusPercentage(bookingBonus.percentage || 0);
        } else {
          setBookingBonusEnabled(false);
          setBookingBonusPercentage(0);
        }
        
        // Load service bonus
        if (serviceBonus) {
          setServiceBonusEnabled(serviceBonus.enabled || false);
          setServiceBonusItems(serviceBonus.services || []);
        } else {
          setServiceBonusEnabled(false);
          setServiceBonusItems([]);
        }

        const upsell = bonusConfig?.upsell_bonus;
        if (upsell) {
          setUpsellEnabled(upsell.enabled || false);
          setUpsellPayout(upsell.payout === 'split' ? 'split' : 'creator');
          setUpsellApplyTo(upsell.apply_to === 'selected' ? 'selected' : 'anything');
          setUpsellPercentage(upsell.percentage || 0);
          setUpsellServices(upsell.services || []);
          setUpsellAddons(upsell.addons || []);
        } else {
          setUpsellEnabled(false);
          setUpsellPayout('creator');
          setUpsellApplyTo('anything');
          setUpsellPercentage(0);
          setUpsellServices([]);
          setUpsellAddons([]);
        }
      } catch (err) {
        console.error('Error loading bonus configuration:', err);
        setBookingBonusEnabled(false);
        setBookingBonusPercentage(0);
        setServiceBonusEnabled(false);
        setServiceBonusItems([]);
      }
    };
    
    loadBonusConfiguration();
  }, [resource?.id, isBonusModalOpen]);

  // Load bonus configuration for main display (regardless of modal state)
  useEffect(() => {
    const loadBonusConfigurationForDisplay = async () => {
      if (!resource?.id) return;
      
      try {
        const { data: dbResource, error } = await supabase
          .from('resources')
          .select('bonus_configuration')
          .eq('id', resource.id)
          .single();
        
        if (!error && dbResource?.bonus_configuration) {
          const bonusConfig = dbResource.bonus_configuration;
          
          // Load booking bonus for display
          if (bonusConfig.booking_bonus) {
            const bookingBonus = bonusConfig.booking_bonus;
            setBookingBonusEnabled(bookingBonus.enabled || false);
            setBookingBonusPercentage(bookingBonus.percentage || 0);
          }
          
          // Load service bonus for display  
          if (bonusConfig.service_bonus) {
            const serviceBonus = bonusConfig.service_bonus;
            setServiceBonusEnabled(serviceBonus.enabled || false);
            setServiceBonusItems(serviceBonus.services || []);
          }
          if (bonusConfig.upsell_bonus) {
            const upsell = bonusConfig.upsell_bonus;
            setUpsellEnabled(upsell.enabled || false);
            setUpsellPayout(upsell.payout === 'split' ? 'split' : 'creator');
            setUpsellApplyTo(upsell.apply_to === 'selected' ? 'selected' : 'anything');
            setUpsellPercentage(upsell.percentage || 0);
            setUpsellServices(upsell.services || []);
            setUpsellAddons(upsell.addons || []);
          }
        }
      } catch (err) {
        console.error('Error loading bonus configuration for display:', err);
      }
    };
    
    loadBonusConfigurationForDisplay();
  }, [resource?.id]);

  // Helper functions for service bonus management
  const addServiceBonusItem = () => {
    const availableService = availableServices.find(service => 
      !serviceBonusItems.some(item => item.serviceId === service.id)
    );
    
    if (availableService) {
      setServiceBonusItems([...serviceBonusItems, { 
        serviceId: availableService.id, 
        percentage: 0 
      }]);
    }
  };

  const removeServiceBonusItem = (index: number) => {
    setServiceBonusItems(serviceBonusItems.filter((_, i) => i !== index));
  };

  const updateServiceBonusPercentage = (index: number, percentage: number) => {
    const updatedItems = [...serviceBonusItems];
    updatedItems[index] = { ...updatedItems[index], percentage };
    setServiceBonusItems(updatedItems);
  };

  const updateServiceBonusService = (index: number, serviceId: string) => {
    const updatedItems = [...serviceBonusItems];
    updatedItems[index] = { ...updatedItems[index], serviceId };
    setServiceBonusItems(updatedItems);
  };

  const addUpsellService = () => {
    const availableService = availableServices.find(service =>
      !upsellServices.some(item => item.serviceId === service.id)
    );
    if (availableService) {
      setUpsellServices([...upsellServices, { serviceId: availableService.id, percentage: 0 }]);
    }
  };

  const addUpsellAddon = () => {
    const availableAddon = availableAddons.find(addon =>
      !upsellAddons.some(item => item.addonId === addon.id)
    );
    if (availableAddon) {
      setUpsellAddons([...upsellAddons, { addonId: availableAddon.id, percentage: 0 }]);
    }
  };

  const handleSaveBonusConfiguration = async () => {
    try {
      const bonusConfiguration = {
        ...resource?.bonus_configuration,
        booking_bonus: {
          enabled: bookingBonusEnabled,
          percentage: bookingBonusPercentage
        },
        service_bonus: {
          enabled: serviceBonusEnabled,
          services: serviceBonusItems
        },
        upsell_bonus: {
          enabled: upsellEnabled,
          payout: upsellPayout,
          apply_to: upsellApplyTo,
          percentage: upsellPercentage,
          services: upsellServices,
          addons: upsellAddons
        }
      };

      // Update the database directly
      const { error: updateError } = await supabase
        .from('resources')
        .update({ bonus_configuration: bonusConfiguration })
        .eq('id', resource.id);
      
      if (updateError) {
        throw updateError;
      }
      // Close the modal
      setIsBonusModalOpen(false);
    } catch (error) {
      console.error('Error saving bonus configuration:', error);
    }
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const bonusModalContent = (
    <div 
      className="fixed inset-0 transition-opacity duration-200 bg-black/50"
      style={{
        zIndex: 100010,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
      onClick={() => setIsBonusModalOpen(false)}
    >
      <div 
        className={`bg-white shadow-2xl flex flex-col overflow-hidden transform transition-all duration-200 ${
          isMobile 
            ? `relative w-full h-full ${isBonusModalOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] ${
                isBonusModalOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100050
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={() => setIsBonusModalOpen(false)}
          className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header */}
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
          <h2 className="text-2xl font-bold text-gray-900" style={{ 
            letterSpacing: '-0.02em'
          }}>
            Configure Bonuses
          </h2>
        </div>
        
        <div 
          className="flex-1 overflow-y-auto bg-white"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6`}>
            {/* Active Bonuses Summary */}
            <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Currently Active Bonuses</h3>
              <div className="space-y-2">
                {bookingBonusEnabled ? (
                  <div className="flex items-center justify-between py-2 px-3 bg-green-50 border border-green-200/60 rounded-xl">
                    <div className="flex items-center space-x-2">
                      <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm text-green-800 font-semibold">Booking Bonus</span>
                    </div>
                    <span className="text-sm text-green-700 font-semibold">{bookingBonusPercentage}%</span>
                  </div>
                ) : null}
                
                {upsellEnabled ? (
                  <div className="py-2 px-3 bg-purple-50 border border-purple-200/60 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="h-2 w-2 bg-purple-500 rounded-full"></div>
                        <span className="text-sm text-purple-800 font-semibold">Upsell Bonus</span>
                      </div>
                      <span className="text-sm text-purple-700 font-semibold">
                        {upsellApplyTo === 'anything' ? `${upsellPercentage}%` : 'Selected'}
                      </span>
                    </div>
                  </div>
                ) : null}

                {serviceBonusEnabled && serviceBonusItems.length > 0 ? (
                  <div className="py-2 px-3 bg-blue-50 border border-blue-200/60 rounded-xl">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="h-2 w-2 bg-blue-500 rounded-full"></div>
                      <span className="text-sm text-blue-800 font-semibold">Service Bonuses</span>
                    </div>
                    <div className="space-y-1 ml-4">
                      {serviceBonusItems.map((item, index) => {
                        const service = availableServices.find(s => s.id === item.serviceId);
                        return (
                          <div key={index} className="flex items-center justify-between text-xs">
                            <span className="text-blue-700">{service?.name || 'Unknown Service'}</span>
                            <span className="text-blue-700 font-semibold">{item.percentage}%</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
                
                {!bookingBonusEnabled && !serviceBonusEnabled && !upsellEnabled && (
                  <div className="text-sm text-gray-500 italic py-2">
                    No active bonuses configured
                  </div>
                )}
              </div>
            </div>

            {/* Booking Bonus Section */}
            <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
              <div className="flex items-start space-x-3">
                {/* Checkbox */}
                <div className="flex items-center h-6 mt-1">
                  <input
                    id="booking-bonus"
                    type="checkbox"
                    checked={bookingBonusEnabled}
                    onChange={(e) => setBookingBonusEnabled(e.target.checked)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </div>
                
                {/* Content */}
                <div className="flex-1">
                  <label htmlFor="booking-bonus" className="block">
                    <h3 className="text-base font-semibold text-gray-900 cursor-pointer">
                      Booking Bonus
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Earn a bonus on each booking completed
                    </p>
                  </label>
                  
                  {/* Percentage Input - Only show when enabled */}
                  {bookingBonusEnabled && (
                    <div className="mt-4">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Bonus Percentage
                      </label>
                      <div className="relative max-w-32">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={bookingBonusPercentage || ''}
                          onChange={(e) => setBookingBonusPercentage(parseFloat(e.target.value) || 0)}
                          className="w-full pl-3 pr-8 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                          style={{ fontSize: '16px' }}
                          placeholder="0.0"
                        />
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 text-sm">%</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Service Bonus Section */}
            <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
              <div className="flex items-start space-x-3">
                {/* Checkbox */}
                <div className="flex items-center h-6 mt-1">
                  <input
                    id="service-bonus"
                    type="checkbox"
                    checked={serviceBonusEnabled}
                    onChange={(e) => setServiceBonusEnabled(e.target.checked)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </div>
                
                {/* Content */}
                <div className="flex-1">
                  <label htmlFor="service-bonus" className="block">
                    <h3 className="text-base font-semibold text-gray-900 cursor-pointer">
                      Service Bonus
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Earn different bonus percentages based on specific services
                    </p>
                  </label>
                  
                  {/* Service Selection - Only show when enabled */}
                  {serviceBonusEnabled && (
                    <div className="mt-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <label className="block text-sm font-medium text-gray-700">
                          Assign Services
                        </label>
                        <button
                          type="button"
                          onClick={addServiceBonusItem}
                          disabled={serviceBonusItems.length >= availableServices.length}
                          className="inline-flex items-center px-3 py-2 text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 active:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.98] touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add Service
                        </button>
                      </div>
                      
                      {/* Service Bonus Items */}
                      <div className="space-y-3">
                        {serviceBonusItems.map((item, index) => (
                          <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl border border-gray-200/60">
                            {/* Service Selection */}
                            <div className="flex-1">
                              <select
                                value={item.serviceId}
                                onChange={(e) => updateServiceBonusService(index, e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                                style={{ fontSize: '16px' }}
                              >
                                {availableServices.map((service) => (
                                  <option 
                                    key={service.id} 
                                    value={service.id}
                                    disabled={serviceBonusItems.some((otherItem, otherIndex) => 
                                      otherIndex !== index && otherItem.serviceId === service.id
                                    )}
                                  >
                                    {service.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            
                            {/* Percentage Input */}
                            <div className="relative w-24">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.1"
                                value={item.percentage || ''}
                                onChange={(e) => updateServiceBonusPercentage(index, parseFloat(e.target.value) || 0)}
                                className="w-full pl-3 pr-6 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                                style={{ fontSize: '16px' }}
                                placeholder="0.0"
                              />
                              <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                                <span className="text-gray-500 text-xs">%</span>
                              </div>
                            </div>
                            
                            {/* Remove Button */}
                            <button
                              type="button"
                              onClick={() => removeServiceBonusItem(index)}
                              className="p-2 text-red-500 hover:text-red-600 bg-red-50 hover:bg-red-100 active:bg-red-200 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                        
                        {serviceBonusItems.length === 0 && (
                          <div className="text-sm text-gray-500 italic text-center py-4">
                            No services assigned. Click "Add Service" to get started.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
              <div className="flex items-start space-x-3">
                <div className="flex items-center h-6 mt-1">
                  <input
                    id="upsell-bonus"
                    type="checkbox"
                    checked={upsellEnabled}
                    onChange={(e) => setUpsellEnabled(e.target.checked)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </div>
                <div className="flex-1">
                  <label htmlFor="upsell-bonus" className="block">
                    <h3 className="text-base font-semibold text-gray-900 cursor-pointer">Upsell Bonus</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Earn a bonus on services and add-ons added after the booking is created. The original booking is not included.
                    </p>
                  </label>
                  {upsellEnabled && (
                    <div className="mt-4 space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Who gets paid</label>
                        <select
                          value={upsellPayout}
                          onChange={(e) => setUpsellPayout(e.target.value === 'split' ? 'split' : 'creator')}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                          style={{ fontSize: '16px' }}
                        >
                          <option value="creator">Only the person who added it</option>
                          <option value="split">Split across everyone on the job</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">What counts</label>
                        <select
                          value={upsellApplyTo}
                          onChange={(e) => setUpsellApplyTo(e.target.value === 'selected' ? 'selected' : 'anything')}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                          style={{ fontSize: '16px' }}
                        >
                          <option value="anything">Anything added</option>
                          <option value="selected">Selected services and add-ons</option>
                        </select>
                      </div>
                      {upsellApplyTo === 'anything' && (
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Bonus Percentage</label>
                          <div className="relative max-w-32">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={upsellPercentage || ''}
                              onChange={(e) => setUpsellPercentage(parseFloat(e.target.value) || 0)}
                              className="w-full pl-3 pr-8 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                              style={{ fontSize: '16px' }}
                              placeholder="0.0"
                            />
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 text-sm">%</span>
                            </div>
                          </div>
                        </div>
                      )}
                      {upsellApplyTo === 'selected' && (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-700">Services</label>
                            <button
                              type="button"
                              onClick={addUpsellService}
                              disabled={upsellServices.length >= availableServices.length}
                              className="inline-flex items-center px-3 py-2 text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 disabled:opacity-50"
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Add Service
                            </button>
                          </div>
                          {upsellServices.map((item, index) => (
                            <div key={`upsell-service-${index}`} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl border border-gray-200/60">
                              <select
                                value={item.serviceId}
                                onChange={(e) => {
                                  const next = [...upsellServices];
                                  next[index] = { ...next[index], serviceId: e.target.value };
                                  setUpsellServices(next);
                                }}
                                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl"
                                style={{ fontSize: '16px' }}
                              >
                                {availableServices.map((service) => (
                                  <option key={service.id} value={service.id} disabled={upsellServices.some((other, otherIndex) => otherIndex !== index && other.serviceId === service.id)}>
                                    {service.name}
                                  </option>
                                ))}
                              </select>
                              <div className="relative w-24">
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.1"
                                  value={item.percentage || ''}
                                  onChange={(e) => {
                                    const next = [...upsellServices];
                                    next[index] = { ...next[index], percentage: parseFloat(e.target.value) || 0 };
                                    setUpsellServices(next);
                                  }}
                                  className="w-full pl-3 pr-6 py-3 border border-gray-300 rounded-xl"
                                  style={{ fontSize: '16px' }}
                                />
                                <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                                  <span className="text-gray-500 text-xs">%</span>
                                </div>
                              </div>
                              <button type="button" onClick={() => setUpsellServices(upsellServices.filter((_, i) => i !== index))} className="p-2 text-red-500 bg-red-50 rounded-xl">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                          <div className="flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-700">Add-ons</label>
                            <button
                              type="button"
                              onClick={addUpsellAddon}
                              disabled={availableAddons.length === 0 || upsellAddons.length >= availableAddons.length}
                              className="inline-flex items-center px-3 py-2 text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 disabled:opacity-50"
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Add Add-on
                            </button>
                          </div>
                          {availableAddons.length === 0 && (
                            <p className="text-sm text-gray-500 italic">No add-ons in the library yet.</p>
                          )}
                          {upsellAddons.map((item, index) => (
                            <div key={`upsell-addon-${index}`} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl border border-gray-200/60">
                              <select
                                value={item.addonId}
                                onChange={(e) => {
                                  const next = [...upsellAddons];
                                  next[index] = { ...next[index], addonId: e.target.value };
                                  setUpsellAddons(next);
                                }}
                                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl"
                                style={{ fontSize: '16px' }}
                              >
                                {availableAddons.map((addon) => (
                                  <option key={addon.id} value={addon.id} disabled={upsellAddons.some((other, otherIndex) => otherIndex !== index && other.addonId === addon.id)}>
                                    {addon.name}
                                  </option>
                                ))}
                              </select>
                              <div className="relative w-24">
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.1"
                                  value={item.percentage || ''}
                                  onChange={(e) => {
                                    const next = [...upsellAddons];
                                    next[index] = { ...next[index], percentage: parseFloat(e.target.value) || 0 };
                                    setUpsellAddons(next);
                                  }}
                                  className="w-full pl-3 pr-6 py-3 border border-gray-300 rounded-xl"
                                  style={{ fontSize: '16px' }}
                                />
                                <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
                                  <span className="text-gray-500 text-xs">%</span>
                                </div>
                              </div>
                              <button type="button" onClick={() => setUpsellAddons(upsellAddons.filter((_, i) => i !== index))} className="p-2 text-red-500 bg-red-50 rounded-xl">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-white">
          <button
            onClick={() => setIsBonusModalOpen(false)}
            className="px-4 py-3 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 active:bg-gray-300 active:scale-[0.98] transition-all duration-150 touch-manipulation"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSaveBonusConfiguration}
            className="px-4 py-3 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 active:scale-[0.98] transition-all duration-150 touch-manipulation"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
        <h3 className="text-base font-semibold text-gray-900">Compensation</h3>
        {editMode === 'compensation' ? (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onEdit('')}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => onSave('compensation')}
              disabled={loading}
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {loading ? (
                <>
                  <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </button>
          </div>
        ) : (
          <button
            onClick={() => onEdit('compensation')}
            className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100/50 hover:bg-gray-200/50 text-gray-600 hover:text-gray-700 transition-all duration-200 ease-out transform hover:scale-105 active:scale-95 touch-manipulation"
          >
            <Pencil className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className="p-2 sm:p-6">
        <div className="space-y-4 sm:space-y-6">
          {editMode === 'compensation' ? (
            <>
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  Compensation Type
                </label>
                <select
                  value={formData.compensation_type || 'hourly'}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    compensation_type: e.target.value as 'hourly' | 'flat_rate' | 'commission'
                  })}
                  className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                  style={{ 
                    minHeight: '44px',
                    fontSize: '16px', // Prevent iOS zoom
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)'
                  }}
                >
                  <option value="hourly">Hourly Rate</option>
                  <option value="flat_rate">Flat Rate</option>
                  <option value="commission">Commission</option>
                </select>
              </div>

              {formData.compensation_type === 'hourly' && (
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                    Hourly Rate
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <span className="text-gray-500 text-base font-medium">$</span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.hourly_rate || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        hourly_rate: parseFloat(e.target.value) 
                      })}
                      className="w-full pl-8 pr-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                      style={{ 
                        minHeight: '44px',
                        fontSize: '16px', // Prevent iOS zoom
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)'
                      }}
                    />
                  </div>
                </div>
              )}

              {formData.compensation_type === 'flat_rate' && (
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                    Flat Rate Amount
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <span className="text-gray-500 text-base font-medium">$</span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.flat_rate_amount || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        flat_rate_amount: parseFloat(e.target.value) 
                      })}
                      className="w-full pl-8 pr-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                      style={{ 
                        minHeight: '44px',
                        fontSize: '16px', // Prevent iOS zoom
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)'
                      }}
                    />
                  </div>
                </div>
              )}

              {formData.compensation_type === 'commission' && (
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                    Commission Percentage
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.1"
                      value={formData.commission_percentage || formData.commission_rate || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        commission_percentage: parseFloat(e.target.value),
                        commission_rate: parseFloat(e.target.value)
                      })}
                      className="w-full pl-4 pr-8 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                      style={{ 
                        minHeight: '44px',
                        fontSize: '16px', // Prevent iOS zoom
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)'
                      }}
                    />
                    <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                      <span className="text-gray-500 text-base font-medium">%</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div>
                <div className="text-xs sm:text-sm text-gray-600 font-medium">Compensation Type</div>
                <div className="text-sm sm:text-base text-gray-900 mt-1 font-semibold capitalize">
                  {(resource.compensation_type || 'hourly').replace('_', ' ')}
                </div>
              </div>

              {(resource.compensation_type === 'hourly' || !resource.compensation_type) && (
                <div>
                  <div className="text-xs sm:text-sm text-gray-600 font-medium">Hourly Rate</div>
                  <div className="text-sm sm:text-base text-gray-900 mt-1 font-semibold">
                    ${(resource.hourly_rate || 0).toFixed(2)}/hour
                  </div>
                </div>
              )}

              {resource.compensation_type === 'flat_rate' && (
                <div>
                  <div className="text-xs sm:text-sm text-gray-600 font-medium">Flat Rate Amount</div>
                  <div className="text-sm sm:text-base text-gray-900 mt-1 font-semibold">
                    ${(resource.flat_rate_amount || 0).toFixed(2)}
                  </div>
                </div>
              )}

              {resource.compensation_type === 'commission' && (
                <div>
                  <div className="text-xs sm:text-sm text-gray-600 font-medium">Commission Rate</div>
                  <div className="text-sm sm:text-base text-gray-900 mt-1 font-semibold">
                    {(resource.commission_percentage || resource.commission_rate || 0)}%
                  </div>
                </div>
              )}
              
              {/* Configure Bonuses Section */}
              <div className="border-t border-gray-200/50 pt-4 mt-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="text-xs sm:text-sm text-gray-600 font-medium">Configure Bonuses</div>
                    <div className="text-xs text-gray-500 mt-0.5">Set up performance and milestone bonuses</div>
                    
                    {/* Active Bonuses Display */}
                    {(bookingBonusEnabled || serviceBonusEnabled || upsellEnabled) && (
                      <div className="mt-2 space-y-2">
                        {bookingBonusEnabled && (
                          <div className="inline-flex items-center space-x-2 py-1 px-2 bg-green-50 border border-green-200 rounded-lg">
                            <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                            <span className="text-xs text-green-800 font-medium">Booking Bonus</span>
                            <span className="text-xs text-green-700 font-semibold">{bookingBonusPercentage}%</span>
                          </div>
                        )}
                        {upsellEnabled && (
                          <div className="inline-flex items-center space-x-2 py-1 px-2 bg-purple-50 border border-purple-200 rounded-lg">
                            <div className="h-2 w-2 bg-purple-500 rounded-full"></div>
                            <span className="text-xs text-purple-800 font-medium">Upsell Bonus</span>
                            <span className="text-xs text-purple-700 font-semibold">
                              {upsellApplyTo === 'anything' ? `${upsellPercentage}%` : 'Selected'}
                            </span>
                          </div>
                        )}
                        {serviceBonusEnabled && serviceBonusItems.length > 0 && (
                          <div className="inline-flex items-center space-x-2 py-1 px-2 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="h-2 w-2 bg-blue-500 rounded-full"></div>
                            <span className="text-xs text-blue-800 font-medium">Service Bonuses</span>
                            <span className="text-xs text-blue-700 font-semibold">
                              {serviceBonusItems.length} service{serviceBonusItems.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsBonusModalOpen(true)}
                    className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    Configure
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Bonus Configuration Modal */}
      {isBonusModalOpen && createPortal(bonusModalContent, document.body)}
    </div>
  );
}